// src/components/GJMSConfig.jsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
    Typography, Box,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TablePagination, IconButton, Stack, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Button, Snackbar, Alert,
    CircularProgress, Menu, MenuItem
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from '@mui/icons-material/Close';

import { database } from "../firebase"; 
import { ref, push, set, onValue, update, remove, get } from "firebase/database";

// Helper: ubah onValue menjadi Promise (untuk get single value)
const onValuePromise = (refTarget) => {
    return new Promise((resolve, reject) => {
        onValue(
            refTarget,
            (snapshot) => resolve(snapshot),
            (error) => reject(error),
            { onlyOnce: true }
        );
    });
};

function GJMSConfig({ title, dbPath, fieldName, primaryKeyField }) {
    const [dataList, setDataList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [openDialog, setOpenDialog] = useState(false);
    const [menuAnchor, setMenuAnchor] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [currentFirebaseKey, setCurrentFirebaseKey] = useState(null); // Key dari Firebase

    // State untuk form input GeoJSON properties
    // Menggunakan objek untuk properti agar lebih dinamis
    const [propertiesForm, setPropertiesForm] = useState({});
    const [geometry, setGeometry] = useState(''); 

    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "info",
    });

    const fileInputRef = useRef(null);

    // Tentukan kolom yang akan ditampilkan berdasarkan dbPath
    const getTableColumns = useCallback(() => {
        if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
            return [
                { id: 'firebaseKey', label: 'Firebase Key' },
                { id: 'koordinat', label: 'Koordinat' },
                { id: 'lokasi', label: 'Lokasi' },
                { id: 'geometryType', label: 'Geometry Type' },
            ];
        } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
            return [
                { id: 'firebaseKey', label: 'Firebase Key' },
                { id: 'Id', label: 'ID Jalan' },
                { id: 'Panjang', label: 'Panjang (m)' },
                { id: 'Ket', label: 'Keterangan' },
                { id: 'geometryType', label: 'Geometry Type' },
            ];
        } else if (dbPath === "db_peta/tb_geojson/tb_lebung_area") {
            return [
                { id: 'firebaseKey', label: 'Firebase Key' },
                { id: 'Id', label: 'ID' },
                { id: 'luas', label: 'Luas' },
                { id: 'Nama', label: 'Nama' },
                { id: 'geometryType', label: 'Geometry Type' },
            ];
        } else if (dbPath === "db_peta/tb_geojson/tb_pipa_gas") {
            return [
                { id: 'firebaseKey', label: 'Firebase Key' },
                { id: 'Id', label: 'ID' },
                { id: 'geometryType', label: 'Geometry Type' },
            ];
        } else if (dbPath === "db_peta/tb_geojson/tb_pos_keamanan") {
            return [
                { id: 'firebaseKey', label: 'Firebase Key' },
                { id: 'Id', label: 'ID' },
                { id: 'Pos', label: 'Pos' },
                { id: 'geometryType', label: 'Geometry Type' },
            ];
        } 
        return []; // Default empty
    }, [dbPath]);

    // Ambil data dari Firebase Realtime Database
    useEffect(() => {
        const tbRef = ref(database, dbPath);
        setLoading(true);

        const unsubscribe = onValue(
            tbRef,
            (snapshot) => {
                const data = snapshot.val();
                if (snapshot.exists()) {
                    // Data GeoJSON disimpan dengan key Firebase
                    const list = Object.entries(data).map(([key, value]) => {
                        // Pastikan properti yang diambil sesuai dengan struktur yang disimpan
                        const item = {
                            firebaseKey: key,
                            geometry: value.geometry,
                            geometryType: value.geometry?.type || 'N/A',
                            originalFeature: value
                        };
                        // Tambahkan properti dinamis
                        if (value.properties) {
                            Object.keys(value.properties).forEach(propKey => {
                                item[propKey] = value.properties[propKey];
                            });
                        }
                        return item;
                    });
                    setDataList(list);
                } else {
                    setDataList([]);
                }
                setLoading(false);
            },
            (error) => {
                console.error("Error:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal memuat data ${title.toLowerCase()}.`,
                    severity: "error",
                });
                setLoading(false);
            }
        );

        return () => unsubscribe();
    }, [dbPath, title]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleOpenDialog = () => {
        setIsEditing(false);
        setPropertiesForm({}); // Clear all properties
        setGeometry(""); 
        setCurrentFirebaseKey(null);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setPropertiesForm({});
        setGeometry("");
        setIsEditing(false);
        setCurrentFirebaseKey(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleSave = async () => {
        // Validasi dasar
        if (!geometry.trim()) {
            setSnackbar({
                open: true,
                message: `Geometry tidak boleh kosong.`,
                severity: "warning",
            });
            return;
        }
        
        // Validasi properti berdasarkan tab
        if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
            if (!propertiesForm.koordinat || !propertiesForm.lokasi) {
                setSnackbar({
                    open: true,
                    message: `Koordinat dan Lokasi tidak boleh kosong.`,
                    severity: "warning",
                });
                return;
            }
        } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
            if (propertiesForm.Id === undefined || propertiesForm.Id === null) {
                setSnackbar({
                    open: true,
                    message: `ID Jalan tidak boleh kosong.`,
                    severity: "warning",
                });
                return;
            }
        }


        try {
            const geojsonBaseRef = ref(database, dbPath);
            let targetFirebaseKey = currentFirebaseKey;

            const newFeatureData = {
                type: "Feature",
                properties: { ...propertiesForm }, // Gunakan objek propertiesForm
                geometry: JSON.parse(geometry) 
            };

            // Sanitasi data untuk Realtime Database (menghapus array kosong/null)
            const finalFeature = JSON.parse(JSON.stringify(newFeatureData, (key, value) => {
                if (Array.isArray(value) && value.length === 0) {
                    return undefined;
                }
                return value;
            }));

            if (isEditing) {
                // Update data yang sudah ada
                await update(ref(database, `${dbPath}/${targetFirebaseKey}`), finalFeature);
                setSnackbar({
                    open: true,
                    message: `Data GeoJSON berhasil diperbarui.`,
                    severity: "success",
                });
            } else {
                // Logika unik untuk pencarian keberadaan fitur berdasarkan koordinat atau ID Jalan
                const existingSnapshot = await get(geojsonBaseRef);
                const existingGeojson = existingSnapshot.val() || {};
                let foundExisting = false;
                let existingKeyForMatch = null;

                if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
                    // Cari berdasarkan koordinat untuk GeoJSON Lokasi
                    Object.entries(existingGeojson).forEach(([key, feature]) => {
                        if (feature.properties && String(feature.properties.koordinat) === String(propertiesForm.koordinat)) {
                            foundExisting = true;
                            existingKeyForMatch = key;
                        }
                    });
                } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
                    // Cari berdasarkan Id untuk GeoJSON Jalan Utama
                    Object.entries(existingGeojson).forEach(([key, feature]) => {
                        if (feature.properties && String(feature.properties.Id) === String(propertiesForm.Id)) {
                            foundExisting = true;
                            existingKeyForMatch = key;
                        }
                    });
                }

                if (foundExisting) {
                    await update(ref(database, `${dbPath}/${existingKeyForMatch}`), finalFeature);
                    setSnackbar({
                        open: true,
                        message: `Data GeoJSON dengan ID/Koordinat yang sama berhasil diperbarui.`,
                        severity: "success",
                    });
                } else {
                    const newRef = push(geojsonBaseRef);
                    await set(newRef, finalFeature);
                    setSnackbar({
                        open: true,
                        message: `Data GeoJSON baru berhasil disimpan.`,
                        severity: "success",
                    });
                }
            }
            handleCloseDialog();
        } catch (error) {
            console.error("Gagal simpan/update GeoJSON:", error);
            setSnackbar({
                open: true,
                message: `Gagal menyimpan/memperbarui data GeoJSON: ${error.message}.`,
                severity: "error",
            });
        }
    };

    const handleImportButtonClick = () => {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    };

    const handleImportGeoJSON = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        if (file.type !== 'application/geo+json' && !file.name.endsWith('.geojson')) {
            setSnackbar({
                open: true,
                message: 'File harus berformat GeoJSON (.geojson)!',
                severity: "warning",
            });
            event.target.value = null;
            return;
        }

        setSnackbar({
            open: true,
            message: 'Membaca file GeoJSON...',
            severity: "info",
        });

        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const geojsonContent = JSON.parse(e.target.result);
                if (geojsonContent.type !== "FeatureCollection" || !Array.isArray(geojsonContent.features)) {
                    throw new Error("File GeoJSON tidak valid: Bukan FeatureCollection atau tidak memiliki fitur.");
                }

                setSnackbar({
                    open: true,
                    message: 'Mengkonversi dan menyimpan ke Realtime Database...',
                    severity: "info",
                });

                const geojsonBaseRef = ref(database, dbPath); 
                
                const existingSnapshot = await get(geojsonBaseRef);
                const existingGeojson = existingSnapshot.val() || {}; 

                const updates = {};
                const existingMatchMap = new Map(); // Map: unique_identifier -> firebaseKey

                // Bangun map dari data yang sudah ada ke firebaseKey mereka
                Object.entries(existingGeojson).forEach(([key, feature]) => {
                    if (dbPath === "db_peta/tb_geojson/tb_lokasi" && feature.properties && feature.properties.koordinat) {
                        existingMatchMap.set(String(feature.properties.koordinat), key);
                    } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama" && feature.properties && feature.properties.Id !== undefined && feature.properties.Id !== null) {
                        existingMatchMap.set(String(feature.properties.Id), key);
                    }
                });

                geojsonContent.features.forEach((feature) => {
                    let uniqueIdentifierFromGeoJSON;
                    let transformedProperties = {};

                    // Transformasi properti berdasarkan dbPath
                    if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
                        if (feature.properties.Lokasi === undefined || feature.properties.Lokasi === null) {
                            console.warn("Fitur GeoJSON tanpa properti 'Lokasi' akan dilewati:", feature);
                            return;
                        }
                        if (feature.properties.Koordinat === undefined || feature.properties.Koordinat === null) {
                            console.warn("Fitur GeoJSON tanpa properti 'Koordinat' akan dilewati:", feature);
                            return;
                        }
                        transformedProperties = {
                            koordinat: String(feature.properties.Koordinat),
                            lokasi: String(feature.properties.Lokasi)
                        };
                        uniqueIdentifierFromGeoJSON = transformedProperties.koordinat;
                    } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
                        if (feature.properties.Id === undefined || feature.properties.Id === null) {
                            console.warn("Fitur GeoJSON tanpa properti 'Id' akan dilewati:", feature);
                            return;
                        }
                        transformedProperties = {
                            Id: String(feature.properties.Id), // Pastikan Id adalah string
                            Panjang: feature.properties.Panjang,
                            Ket: feature.properties.Ket
                        };
                        uniqueIdentifierFromGeoJSON = transformedProperties.Id;
                    } else {
                        // Default case if new tab added without specific handling
                        transformedProperties = { ...feature.properties };
                        uniqueIdentifierFromGeoJSON = JSON.stringify(feature.geometry); // Fallback to geometry as identifier
                    }

                    const cleanedFeature = {
                        ...feature,
                        properties: transformedProperties
                    };

                    const finalFeature = JSON.parse(JSON.stringify(cleanedFeature, (key, value) => {
                        if (Array.isArray(value) && value.length === 0) {
                            return undefined;
                        }
                        return value;
                    }));

                    const existingFirebaseKey = existingMatchMap.get(uniqueIdentifierFromGeoJSON);

                    let firebaseKey;
                    if (existingFirebaseKey) {
                        firebaseKey = existingFirebaseKey;
                    } else {
                        const newRef = push(geojsonBaseRef); 
                        firebaseKey = newRef.key; 
                    }
                    updates[`${firebaseKey}`] = finalFeature;
                });

                await update(geojsonBaseRef, updates); 

                setSnackbar({
                    open: true,
                    message: 'Unggah dan simpan GeoJSON berhasil! Peta akan diperbarui.',
                    severity: "success",
                });
            } catch (error) {
                console.error("Gagal impor GeoJSON:", error);
                setSnackbar({
                    open: true,
                    message: `Gagal mengimpor data GeoJSON: ${error.message}.`,
                    severity: "error",
                });
            } finally {
                event.target.value = null; // Reset input file
            }
        };

        reader.readAsText(file);
    };


    const exportToExcel = () => {
        if (!dataList || dataList.length === 0) {
            setSnackbar({
                open: true,
                message: "Tidak ada data untuk diekspor.",
                severity: "warning",
            });
            return;
        }

        const formattedData = dataList.map((item) => {
            const row = {};
            row["Firebase Key"] = item.firebaseKey;
            // Kolom ekspor dinamis
            if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
                row["Koordinat"] = item.koordinat;
                row["Lokasi"] = item.lokasi;
            } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
                row["Id"] = item.Id;
                row["Panjang"] = item.Panjang;
                row["Ket"] = item.Ket;
            }
            row["Geometry"] = JSON.stringify(item.geometry); 
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(formattedData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, title.replace("Daftar ", ""));

        const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
        const blob = new Blob([excelBuffer], { type: "application/octet-stream" });

        saveAs(blob, `${title.replace("Daftar ", "Data_")}.xlsx`);
    };

    const handleMenuClick = (event, row) => {
        setMenuAnchor(event.currentTarget);
        setSelectedRow(row);
    };

    const handleMenuClose = () => {
        setMenuAnchor(null);
        setSelectedRow(null);
    };

    const handleEdit = (row) => {
        handleMenuClose();
        setIsEditing(true);
        setCurrentFirebaseKey(row.firebaseKey);
        // Set properti form berdasarkan dbPath
        if (dbPath === "db_peta/tb_geojson/tb_lokasi") {
            setPropertiesForm({
                koordinat: row.koordinat,
                lokasi: row.lokasi
            });
        } else if (dbPath === "db_peta/tb_geojson/tb_jalan_utama") {
            setPropertiesForm({
                Id: row.Id,
                Panjang: row.Panjang,
                Ket: row.Ket
            });
        }
        setGeometry(JSON.stringify(row.geometry, null, 2)); 
        setOpenDialog(true);
    };

    const handleDelete = (row) => {
        handleMenuClose();
        setSelectedRow(row);
        setOpenDeleteDialog(true);
    };

    const confirmDelete = async () => {
        if (!selectedRow) return;

        try {
            const idToDelete = selectedRow.firebaseKey;
            const itemRef = ref(database, `${dbPath}/${idToDelete}`);
            await remove(itemRef);
            setSnackbar({
                open: true,
                message: `Data GeoJSON berhasil dihapus.`,
                severity: "success",
            });
            setOpenDeleteDialog(false);
            setSelectedRow(null);
        } catch (error) {
            console.error("Gagal hapus:", error);
            setSnackbar({
                open: true,
                message: `Gagal menghapus data GeoJSON.`,
                severity: "error",
            });
        }
    };

    const handleCloseDeleteDialog = () => {
        setOpenDeleteDialog(false);
        setSelectedRow(null);
    };

    const columns = getTableColumns();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'inherit' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                <Typography variant="h6">{title}</Typography>
                <Stack direction="row" spacing={1}>
                    <IconButton color="primary" size="small" onClick={handleOpenDialog}>
                        <AddIcon />
                    </IconButton>
                    <IconButton color="primary" size="small" onClick={handleImportButtonClick}>
                        <FileUploadOutlinedIcon />
                    </IconButton>
                    <input
                        type="file"
                        accept=".geojson,application/geo+json" 
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImportGeoJSON} 
                    />
                    <IconButton color="primary" size="small" onClick={exportToExcel}>
                        <FileDownloadOutlinedIcon />
                    </IconButton>
                </Stack>
            </Stack>

            <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell>Aksi</TableCell>
                            {columns.map((col) => (
                                <TableCell key={col.id}>{col.label}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {dataList.length > 0 ? (
                            dataList
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row) => (
                                    <TableRow key={row.firebaseKey}>
                                        <TableCell>
                                            <IconButton size="small" onClick={(event) => handleMenuClick(event, row)}>
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                        {columns.map((col) => (
                                            <TableCell key={col.id}>
                                                {col.id === 'geometryType' ? (
                                                    row.geometry?.type || 'N/A'
                                                ) : (
                                                    row[col.id]
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1} align="center">
                                    Tidak ada data GeoJSON
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => handleEdit(selectedRow)}>
                    <EditIcon fontSize="small" sx={{ mr: 2 }} />
                    Edit
                </MenuItem>
                <MenuItem onClick={() => handleDelete(selectedRow)}>
                    <DeleteIcon fontSize="small" sx={{ mr: 2 }} />
                    Hapus
                </MenuItem>
            </Menu>

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2 }}>
                <Typography variant="body2">
                    {`${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, dataList.length)} of ${dataList.length}`}
                </Typography>
                <TablePagination
                    component="div"
                    count={dataList.length}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage=""
                    labelDisplayedRows={() => ""}
                    sx={{
                        ".MuiTablePagination-toolbar": {
                            padding: 0,
                            minHeight: "auto",
                        },
                        ".MuiTablePagination-actions": {
                            display: "block",
                        },
                        ".MuiTablePagination-selectLabel": {
                            display: "none",
                        },
                        ".MuiTablePagination-displayedRows": {
                            display: "none",
                        },
                        ".MuiTablePagination-select": {
                            marginLeft: "8px",
                            marginRight: "8px",
                        },
                    }}
                />
            </Box>

            {/* Dialog Tambah/Edit */}
            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm"> 
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                    {isEditing ? `Edit Data GeoJSON` : `Tambah Data GeoJSON`}
                    <IconButton aria-label="close" onClick={handleCloseDialog}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {dbPath === "db_peta/tb_geojson/tb_lokasi" && (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="Koordinat"
                                type="text"
                                fullWidth
                                value={propertiesForm.koordinat || ''}
                                onChange={(e) => setPropertiesForm(prev => ({ ...prev, koordinat: e.target.value }))}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                margin="dense"
                                label="Lokasi"
                                type="text"
                                fullWidth
                                value={propertiesForm.lokasi || ''}
                                onChange={(e) => setPropertiesForm(prev => ({ ...prev, lokasi: e.target.value }))}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                    {dbPath === "db_peta/tb_geojson/tb_jalan_utama" && (
                        <>
                            <TextField
                                autoFocus
                                margin="dense"
                                label="ID Jalan"
                                type="number" // ID bisa berupa angka
                                fullWidth
                                value={propertiesForm.Id || ''}
                                onChange={(e) => setPropertiesForm(prev => ({ ...prev, Id: e.target.value }))}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                margin="dense"
                                label="Panjang (m)"
                                type="number" // Panjang bisa berupa angka
                                fullWidth
                                value={propertiesForm.Panjang || ''}
                                onChange={(e) => setPropertiesForm(prev => ({ ...prev, Panjang: parseFloat(e.target.value) || 0 }))}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                margin="dense"
                                label="Keterangan"
                                type="text"
                                fullWidth
                                value={propertiesForm.Ket || ''}
                                onChange={(e) => setPropertiesForm(prev => ({ ...prev, Ket: e.target.value }))}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}
                    <TextField
                        margin="dense"
                        label="Geometry (JSON)"
                        type="text"
                        fullWidth
                        multiline
                        rows={6} 
                        value={geometry}
                        onChange={(e) => setGeometry(e.target.value)}
                        sx={{ mb: 2 }}
                        placeholder='{"type": "Polygon", "coordinates": [...]}'
                    />
                </DialogContent>
                <DialogActions sx={{ pt: "20px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDialog}>Batal</Button>
                    <Button onClick={handleSave} variant="contained">
                        {isEditing ? "Update" : "Simpan"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Dialog Konfirmasi Hapus Data */}
            <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="xs" fullWidth>
                <DialogTitle>Konfirmasi Hapus</DialogTitle>
                <DialogContent>
                    <Typography>
                        Apakah Anda yakin ingin menghapus data GeoJSON ini?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ pt: "0px", pb: "20px", pl: "24px", pr: "24px" }}>
                    <Button onClick={handleCloseDeleteDialog}>Batal</Button>
                    <Button
                        onClick={confirmDelete}
                        variant="contained"
                        color="error"
                    >
                        Hapus
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default GJMSConfig;
